/**
 * Company data screens: ITCSI support access (M02-07), full export and the
 * sandbox copy (M15-03, M15-17), and data import templates (M15-01).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, DatabaseBackup, FileDown, FlaskConical, LifeBuoy, Upload } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, shortDate } from '../../lib/format';
import { downloadFile, postForm } from '../../lib/files';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, Field, FilterBar, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { errorText } from '../integrations/shared';

interface Session { id: string; reason: string; hours: number; status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'DECLINED'; requestedBy: string; startsAt: string | null; endsAt: string | null; requests: number; createdAt: string }
const SESSION_TONE = { PENDING: 'warning', ACTIVE: 'info', ENDED: 'neutral', DECLINED: 'neutral' } as const;

export function SupportAccessPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['support-sessions'], queryFn: () => api<Session[]>('/company/support-sessions'), refetchInterval: 30_000 });
  const [hours, setHours] = useState<Record<string, number>>({});
  const act = useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: 'approve' | 'decline' | 'end'; body?: unknown }) => api(`/company/support-sessions/${id}/${action}`, { method: 'POST', body: body ?? {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['support-sessions'] }),
  });
  const manage = can('users.manage');
  return (
    <>
      <PageHeader title="ITCSI Support Access" description="ITCSI cannot see your books. When you ask for help, approve a read-only session for a few hours; it ends by itself, and every page support opens is logged." />
      {act.error && <div className="mb-4"><Alert tone="critical">{errorText(act.error)}</Alert></div>}
      {list.isLoading && <Skeleton className="h-32 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No support requests" description="Requests from ITCSI support appear here for you to approve or decline." /></Card>}
      <div className="space-y-3">
        {list.data?.map((s) => (
          <Card
            key={s.id}
            title={`${s.requestedBy} · ${s.status === 'PENDING' ? `asks for ${s.hours} hour${s.hours === 1 ? '' : 's'}` : s.status.toLowerCase()}`}
            description={`Requested ${dateTime(s.createdAt)}${s.endsAt ? ` · ${s.status === 'ACTIVE' ? 'ends' : 'ended'} ${dateTime(s.endsAt)}` : ''}${s.requests ? ` · ${s.requests} pages opened` : ''}`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip tone={SESSION_TONE[s.status]}>{s.status.toLowerCase()}</StatusChip>
                {manage && s.status === 'PENDING' && (
                  <>
                    <select className={filterClass} value={hours[s.id] ?? s.hours} onChange={(e) => setHours({ ...hours, [s.id]: Number(e.target.value) })} aria-label="Hours to allow">
                      {[1, 2, 4, 8, 24].map((h) => <option key={h} value={h}>{h} h</option>)}
                    </select>
                    <Button size="sm" busy={act.isPending} onClick={() => act.mutate({ id: s.id, action: 'approve', body: { hours: hours[s.id] ?? s.hours } })}>Approve read-only</Button>
                    <Button size="sm" variant="secondary" onClick={() => act.mutate({ id: s.id, action: 'decline' })}>Decline</Button>
                  </>
                )}
                {manage && s.status === 'ACTIVE' && <Button size="sm" variant="danger" onClick={() => act.mutate({ id: s.id, action: 'end' })}>End now</Button>}
              </div>
            }
          >
            <p className="text-sm text-slate-700 dark:text-slate-200">“{s.reason}”</p>
          </Card>
        ))}
      </div>
    </>
  );
}

export function DataPage(): JSX.Element {
  const { user, can, switchCompany } = useAuth();
  const isOwner = user?.roleName === 'Owner';
  const status = useQuery({ queryKey: ['sandbox'], queryFn: () => api<{ isSandbox: boolean; sandbox: { id: string; registeredName: string; createdAt: string } | null }>('/company/sandbox'), enabled: can('company.view', 'users.view', 'integrations.manage') });
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const exportAll = useMutation({ mutationFn: () => downloadFile('/company/export'), onSuccess: () => setMessage({ tone: 'ok', text: 'Export downloaded. Keep it for at least ten years.' }), onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }) });
  const create = useMutation({
    mutationFn: () => api<{ companyId: string }>('/company/sandbox', { method: 'POST' }),
    onSuccess: () => { void status.refetch(); setMessage({ tone: 'ok', text: 'Sandbox created. Open it from the company switcher or below.' }); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });
  return (
    <>
      <PageHeader title="Export & Sandbox" description="Take all your data with you at any time, and test integrations on a safe copy." />
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Full company export" description="Every record, attachment and the audit log, in open formats (JSON Lines), with a SHA-256 manifest.">
          <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
            <li>Master data, journals, invoices, receipts, bills, payments, bank and tax records</li>
            <li>All uploaded files, checked against their fingerprints</li>
            <li>No passwords, MFA secrets or API keys</li>
          </ul>
          {isOwner ? <Button busy={exportAll.isPending} onClick={() => exportAll.mutate()}><FileDown aria-hidden="true" className="h-4 w-4" /> Download export (ZIP)</Button> : <p className="text-sm text-slate-500">Only the company Owner can download the full export.</p>}
        </Card>
        <Card title="Sandbox" description="A copy of your set-up with masked customers and suppliers and no transactions. Its documents say “SANDBOX — NOT VALID” and use their own SBX- series.">
          {status.isLoading ? <Skeleton className="h-16 w-full" /> : status.data?.isSandbox ? (
            <Alert tone="warning">You are in the sandbox now. Switch back to the real company from the company switcher.</Alert>
          ) : status.data?.sandbox ? (
            <div className="space-y-3">
              <p className="text-sm"><span className="font-medium">{status.data.sandbox.registeredName}</span> <span className="text-slate-500">· created {shortDate(status.data.sandbox.createdAt)}</span></p>
              <Button variant="secondary" onClick={() => void switchCompany(status.data!.sandbox!.id)}><FlaskConical aria-hidden="true" className="h-4 w-4" /> Open sandbox</Button>
            </div>
          ) : isOwner ? (
            <Button busy={create.isPending} onClick={() => create.mutate()}><FlaskConical aria-hidden="true" className="h-4 w-4" /> Create sandbox</Button>
          ) : (
            <p className="text-sm text-slate-500">The Owner can create one sandbox for the company.</p>
          )}
        </Card>
      </div>
    </>
  );
}

interface Kind { key: string; title: string; note: string; permissions: string[]; columns: { key: string; label: string; required?: boolean; help?: string }[] }
interface Report { kind: string; rows: number; errors: { row: number; field: string; message: string }[]; preview: Record<string, string>[]; columns: string[]; committed: boolean; created?: number }

export function ImportPage(): JSX.Element {
  const { can } = useAuth();
  const kinds = useQuery({ queryKey: ['import-kinds'], queryFn: () => api<Kind[]>('/imports') });
  const allowed = (kinds.data ?? []).filter((k) => can(...k.permissions));
  const [kind, setKind] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [asOf, setAsOf] = useState(`${manilaToday().slice(0, 8)}01`);
  const [post, setPost] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const k = allowed.find((x) => x.key === kind) ?? allowed[0];
  const needsDate = k && ['opening-balances', 'open-ar', 'open-ap'].includes(k.key);
  const send = useMutation({
    mutationFn: (commit: boolean) => {
      const form = new FormData();
      form.append('file', file!);
      const qs = new URLSearchParams({ ...(commit ? { commit: 'true' } : {}), ...(needsDate ? { asOf } : {}), ...(post ? { post: 'true' } : {}) });
      return postForm<Report>(`/imports/${k!.key}?${qs}`, form);
    },
    onSuccess: setReport,
  });
  const template = useMutation({ mutationFn: () => downloadFile(`/imports/${k!.key}/template`) });
  return (
    <>
      <PageHeader title="Import Data" description="Bring your records from Excel, QuickBooks, Xero or Peachtree exports. Each file is checked in full before anything is saved." />
      {kinds.isLoading && <Skeleton className="h-40 w-full" />}
      {k && (
        <div className="grid gap-4 xl:grid-cols-3">
          <Card title="1 · Choose and fill a template" className="xl:col-span-1">
            <div className="space-y-3">
              <Field label="What to import">
                <select className={inputClass} value={k.key} onChange={(e) => { setKind(e.target.value); setReport(null); setFile(null); }}>
                  {allowed.map((x) => <option key={x.key} value={x.key}>{x.title}</option>)}
                </select>
              </Field>
              <p className="text-sm text-slate-600 dark:text-slate-300">{k.note}</p>
              <Button variant="secondary" busy={template.isPending} onClick={() => template.mutate()}><FileDown aria-hidden="true" className="h-4 w-4" /> Download template (.xlsx)</Button>
              <details className="text-sm">
                <summary className="cursor-pointer font-medium">Columns</summary>
                <ul className="mt-2 space-y-1 text-xs">
                  {k.columns.map((c) => <li key={c.key}><code>{c.key}</code>{c.required && <span className="text-red-600"> *</span>} — {c.label}{c.help ? `: ${c.help}` : ''}</li>)}
                </ul>
              </details>
            </div>
          </Card>
          <Card title="2 · Check, then import" className="xl:col-span-2">
            <FilterBar>
              <input type="file" accept=".xlsx,.csv" className={filterClass} onChange={(e) => { setFile(e.target.files?.[0] ?? null); setReport(null); }} aria-label="File to import" />
              {needsDate && <label className="flex items-center gap-2 text-sm">Go-live date <input type="date" className={filterClass} value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label>}
              {k.key === 'journals' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={post} onChange={(e) => setPost(e.target.checked)} /> Post (within my limit)</label>}
              <Button disabled={!file} busy={send.isPending && send.variables === false} onClick={() => send.mutate(false)}><Upload aria-hidden="true" className="h-4 w-4" /> Check file</Button>
            </FilterBar>
            {send.error && <Alert tone="critical">{errorText(send.error)}</Alert>}
            {!report && !send.error && <EmptyState title="No file checked yet" description="Checking never saves anything. You import only when every row is clean." />}
            {report && (
              <div className="space-y-3">
                {report.committed ? (
                  <Alert tone="ok"><span className="inline-flex items-center gap-2"><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Imported: {report.created} created from {report.rows} rows.</span></Alert>
                ) : report.errors.length ? (
                  <Alert tone="critical">{report.errors.length} problem{report.errors.length === 1 ? '' : 's'} in {report.rows} rows. Fix them in the file and check again.</Alert>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Alert tone="ok">All {report.rows} rows are valid.</Alert>
                    <Button busy={send.isPending && send.variables === true} onClick={() => send.mutate(true)}><DatabaseBackup aria-hidden="true" className="h-4 w-4" /> Import {report.rows} rows</Button>
                  </div>
                )}
                {report.errors.length > 0 && (
                  <TableWrap>
                    <thead><tr><Th>Row</Th><Th>Column</Th><Th>Problem</Th></tr></thead>
                    <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                      {report.errors.slice(0, 200).map((e, i) => <tr key={i}><Td className="tabular-nums">{e.row || '—'}</Td><Td><code className="text-xs">{e.field}</code></Td><Td>{e.message}</Td></tr>)}
                    </tbody>
                  </TableWrap>
                )}
                {!report.errors.length && !report.committed && report.preview.length > 0 && (
                  <TableWrap>
                    <thead><tr>{report.columns.map((c) => <Th key={c}>{c}</Th>)}</tr></thead>
                    <tbody className="divide-y divide-slate-100 text-xs dark:divide-slate-800">
                      {report.preview.map((r, i) => <tr key={i}>{report.columns.map((c) => <Td key={c}>{r[c]}</Td>)}</tr>)}
                    </tbody>
                  </TableWrap>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
      {kinds.data && !allowed.length && <Alert tone="critical">Your role cannot import data.</Alert>}
      <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><LifeBuoy aria-hidden="true" className="h-4 w-4" /> Open receivables and payables from the previous system can be collected and paid here, but stay out of this system’s books of accounts and BIR returns — the old system already reported them.</p>
    </>
  );
}
